import { useState } from 'react';
import useUserContext from './useUserContext';
import { GameInstance, BattleshipGameState, GameMove, BattleshipMove } from '../types/types';

/**
 * Custom hook to manage the state and logic for the "Battleship" game page,
 * including making a move and handling input changes.
 * @param gameInstance The current instance of the Battleship game.
 * @returns An object containing the following:
 * - `user`: The current user from the context.
 * - `move`: The current move selected by the player.
 * - `handleMakeMove`: A function to send the player's move to the server via a socket event.
 * - `handleInputChange`: A function to update the move state based on user input.
 */

const useBattleshipGamePage = (gameInstance: GameInstance<BattleshipGameState>) => {
  const { user, socket } = useUserContext();
  const [move, setMove] = useState<BattleshipMove | string>('');

  const handleMakeMove = async () => {
    if (typeof move === 'string') return;

    const battleshipMove: GameMove<BattleshipMove> = {
      playerID: user.username,
      gameID: gameInstance.gameID,
      move,
    };

    socket.emit('makeMove', {
      gameID: gameInstance.gameID,
      move: battleshipMove,
    });

    // Reset move after sending
    setMove('');
  };

  const handleInputChange = (coords: BattleshipMove) => {
    let opponentBoard;

    if (user.username === gameInstance.state.player1) {
      opponentBoard = gameInstance.state.player2Board;
    } else {
      opponentBoard = gameInstance.state.player1Board;
    }

    // Make sure move is valid before setting the move
    if (opponentBoard) {
      // Coordinates should be on the board
      if (coords.x >= opponentBoard.length || coords.y >= opponentBoard.length) {
        return;
      }

      // Coordinates of tile user is trying to hit should not have already been hit
      if (['HIT_EMPTY', 'HIT_SHIP'].includes(opponentBoard[coords.x][coords.y])) {
        return;
      }
    }

    setMove(coords);
  };

  return {
    user,
    move,
    handleMakeMove,
    handleInputChange,
  };
};

export default useBattleshipGamePage;
